import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { HttpStatusCode } from "axios";
import { getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { api, getApiEndpointName } from "_shared/_stores/api";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import {
  VendorRolloutBoardPage,
  vendorRolloutBoardPagination,
  vendorRolloutBoardParams,
  VendorRolloutBoardRequest,
} from "legal-entities/vendor-rollouts/_api/vendor-rollout-board-query";
import VendorRolloutBoardSchema, {
  VendorRolloutBoardJSON,
} from "legal-entities/vendor-rollouts/_schema/vendor-rollout-board-schema";

export const vendorRolloutBoardApi = api.injectEndpoints({
  endpoints: (build) => ({
    getVendorRolloutBoard: build.query<VendorRolloutBoardPage, VendorRolloutBoardRequest>({
      query: (request) => {
        const queryString = vendorRolloutBoardParams(request).toString();
        return queryString ? `legal-entities/vendor-rollouts?${queryString}` : "legal-entities/vendor-rollouts";
      },
      rawResponseSchema: VendorRolloutBoardSchema.VendorRolloutBoardJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (board: VendorRolloutBoardJSON, meta) => ({
        ...board,
        pagination: vendorRolloutBoardPagination(board, meta?.response?.headers.get("x-pagination")),
      }),
      providesTags: ["VendorRolloutBoard"],
      transformErrorResponse: (response: FetchBaseQueryError, meta) => toVendorRolloutBoardError(response, meta),
    }),
  }),
});

export const { useGetVendorRolloutBoardQuery } = vendorRolloutBoardApi;

/**
 * The caller asked for a set they may not read — another client's roll-outs, or every client's
 * without the platform grant. Distinct from a general failure because the page says something
 * different: there is nothing to retry.
 */
export class UnauthorizedVendorRolloutBoardError extends Error {
  public constructor() {
    super("You do not have permission to see these roll-outs.");
  }
}

export class VendorRolloutBoardAPIError extends Error {
  public methodName?: string;
  public error: unknown;

  public constructor(methodName: string | undefined, error: unknown) {
    const endpointName = methodName ? `.${methodName}` : "";
    super(`Error in vendorRolloutBoardApi${endpointName}: ${getErrorMessageFromUnknown(error) ?? "An error occurred"}`);
    this.methodName = methodName;
    this.error = error;
  }
}

export function isUnauthorizedVendorRolloutBoardError(error: unknown): boolean {
  return error instanceof UnauthorizedVendorRolloutBoardError;
}

function toVendorRolloutBoardError(response: FetchBaseQueryError, meta?: unknown): Error {
  if (getApiErrorStatus(response) === HttpStatusCode.Forbidden) {
    return new UnauthorizedVendorRolloutBoardError();
  }
  return new VendorRolloutBoardAPIError(getApiEndpointName(meta), response);
}
